import { useContext } from 'react';
import PropTypes from 'prop-types';
import { params, shape, string, useDataModel } from '@thd-nucleus/data-sources';
import { useConfigService, useStoreId } from '@thd-nucleus/experience-context';
import { useHelmet, RedirectTag } from '@thd-nucleus/thd-helmet';
import { RedirectContext } from './RedirectContext';
import {
  isInvalidItemId,
  isNotFound
} from './rules/pip-redirect-rules';
import {
  isInvalidPaintConfig,
  isInvalidCanonical, isPaintReorderPIPDisabled
} from './rules/paint-config-redirect-rules';
import { handleErrors, ERROR_HANDLING_EXPERIENCE } from '../utils/redirector-utils';

const handler = ({ data, error: err, props, queries }) => {
  const {
    configId,
    itemId,
    bypass,
    disableCanonicalRedirect,
    disablePaintReorderPIPFS,
    pathWithQueryParams,
  } = props;
  let error = err;
  if (queries && queries.length && !err) {
    const productQuery = queries.find((query) => query.operationName === 'product');
    if (productQuery && productQuery.error) {
      error = productQuery.error;
    }
  }

  const pageDisabled = isPaintReorderPIPDisabled(disablePaintReorderPIPFS);
  if (pageDisabled) {
    return new RedirectTag(pageDisabled, bypass);
  }

  const invalidConfigId = isInvalidPaintConfig({ data, configId });
  if (invalidConfigId) {
    return new RedirectTag(invalidConfigId, bypass);
  }

  const invalidItemId = isInvalidItemId({ itemId });
  if (invalidItemId) {
    return new RedirectTag(invalidItemId, bypass);
  }

  const errors = handleErrors(error, ERROR_HANDLING_EXPERIENCE.PRODUCT);
  if (errors) {
    return new RedirectTag(errors, bypass);
  }

  const notFound = isNotFound({ data, itemId });
  if (notFound) {
    return new RedirectTag(notFound, bypass);
  }

  const [path] = pathWithQueryParams.split('?');

  const invalidCanonical = isInvalidCanonical({ data, configId, path, disableCanonicalRedirect });
  if (invalidCanonical) {
    return new RedirectTag(invalidCanonical, props.bypass);
  }
  return null;
};

export const PaintPIPRedirector = (props) => {

  const disableCanonicalRedirectFS = useConfigService('fs-prop:pip-wrapper__redirector--disableCanonicalRedirect');
  const disablePaintReorderPIPFS = useConfigService('fs-disable:pip__paintReorder');
  const storeId = useStoreId();
  const {
    disableCanonicalRedirect,
    configId
  } = props;

  const { pathWithQueryParams, itemId, bypass } = useContext(RedirectContext);

  const handlerProps = {
    disableCanonicalRedirect: typeof disableCanonicalRedirectFS !== 'undefined'
      ? disableCanonicalRedirectFS
      : disableCanonicalRedirect,
    disablePaintReorderPIPFS,
    pathWithQueryParams,
    itemId,
    configId,
    bypass,
  };

  const opts = {
    variables: {
      itemId,
      storeId,
      configId,
      skipPaintDetails: !configId
    },
    skip: !!disablePaintReorderPIPFS
  };
  const { data, error } = useDataModel('product', opts);

  useHelmet('paintPipRedirector', { data, error, props: handlerProps }, handler, [configId, itemId]);

  return null;
};

PaintPIPRedirector.displayName = 'PaintPIPRedirector';

PaintPIPRedirector.propTypes = {
  disableCanonicalRedirect: PropTypes.bool,
  configId: PropTypes.string
};

PaintPIPRedirector.defaultProps = {
  disableCanonicalRedirect: false,
  configId: ''
};

PaintPIPRedirector.dataModel = {
  product: params({ itemId: string().isRequired() }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      canonicalUrl: string()
    }),
    paintDetails: params({ storeId: string().isRequired(), configId: string().isRequired() })
      .shape({
        colorId: string()
      }).skip('skipPaintDetails', false)
  })
};
